import { useEffect, useState } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { apiClient } from '@/services/api-client';
import type { User } from '@/types';
import { Loader2 } from 'lucide-react';

/**
 * OnboardingGuard wraps the onboarding routes.
 * Requires Clerk auth (but not admin role).
 * Redirects to the correct onboarding step based on user state.
 */
export function OnboardingGuard() {
  const { isLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const location = useLocation();

  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchUser() {
      if (!isLoaded || !isSignedIn) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        apiClient.setTokenGetter(getToken);
        const response = await apiClient.get<User>('/api/v1/users/me');

        if (response.success && response.data) {
          setUser(response.data);
        } else {
          setError(response.error?.message || 'Failed to fetch user');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    }

    fetchUser();
  }, [isLoaded, isSignedIn, getToken]);

  if (!isLoaded || isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-destructive">Error: {error}</p>
          <p className="text-sm text-muted-foreground">
            Please try refreshing the page or contact support.
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If onboarding is fully complete: admins → dashboard; customers → the
  // account stub (review F3 — /dashboard would dead-end non-admins on the
  // AdminGuard's /unauthorized redirect).
  if (user.onboardingCompletedAt) {
    const isAdmin = user.role === 'admin' || user.role === 'super_admin';
    return <Navigate to={isAdmin ? '/dashboard' : '/account'} replace />;
  }

  // Determine the correct onboarding step
  const currentPath = location.pathname;

  if (!user.termsAcceptedAt) {
    // Must accept terms first
    if (currentPath !== '/welcome/terms') {
      return <Navigate to="/welcome/terms" replace />;
    }
  } else if (!user.privacyAcceptedAt) {
    // Terms done, must accept privacy
    if (currentPath !== '/welcome/privacy') {
      return <Navigate to="/welcome/privacy" replace />;
    }
  } else if (!user.onboardingCompletedAt) {
    // Both accepted, must complete download step
    if (currentPath !== '/welcome/download') {
      return <Navigate to="/welcome/download" replace />;
    }
  }

  return <Outlet context={{ user, setUser }} />;
}
